import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  Field,
  Fieldset,
  Form,
  MarkdownTextarea,
  Text,
  H2,
} from '@payaca/ambrosia';
import { CHAT_AGENT_INSTRUCTIONS_MAX_LENGTH } from '@payaca/shared-isomorphic/constants/chatAgent';
import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

import useUpdateChatAgentInstructions from '@/api/mutations/account/useUpdateChatAgentInstructions';
import useGetMyAccountSettings from '@/api/queries/me/useGetMyAccountSettings';
import { graphql } from '@/gql';
import CompanySettingsTabBase from './CompanySettingsTabBase';

export const CompanySettingsChatAgent_Account = graphql(`
  fragment CompanySettingsChatAgent_Account on Account {
    id
    chatAgentInstructions
  }
`);

const FORM_SCHEMA = z.object({
  instructions: z
    .string()
    .max(
      CHAT_AGENT_INSTRUCTIONS_MAX_LENGTH,
      `These instructions are too long. Shorten them to ${CHAT_AGENT_INSTRUCTIONS_MAX_LENGTH} characters or fewer.`
    ),
});

type FormType = z.infer<typeof FORM_SCHEMA>;

const CompanySettingsChatAgent: FC = () => {
  const { account } = useGetMyAccountSettings();
  const { mutate, isPending } = useUpdateChatAgentInstructions();

  const formMethods = useForm<FormType>({
    defaultValues: { instructions: '' },
    resolver: zodResolver(FORM_SCHEMA),
  });

  const savedInstructions = account?.chatAgentInstructions ?? '';

  useEffect(() => {
    // Don't clobber unsaved edits if a background refetch lands mid-edit.
    if (formMethods.formState.isDirty) return;
    formMethods.reset({ instructions: savedInstructions });
  }, [formMethods, savedInstructions]);

  const onSubmit = (data: FormType) => {
    const instructions = data.instructions.trim() || null;
    mutate(
      { instructions },
      {
        onSuccess: () => {
          formMethods.reset({ instructions: instructions ?? '' });
        },
      }
    );
  };

  const isDirty = formMethods.formState.isDirty;

  return (
    <Form methods={formMethods} onSubmit={onSubmit}>
      <CompanySettingsTabBase>
        <Card sizeVariant="lg">
          <Card.Body className="flex flex-col gap-4">
            <div>
              <H2>Agent Dave</H2>
              <Text level="supporting" colour="primary">
                Add instructions to tailor how Agent Dave (the AI assistant)
                responds for your team, included in every Dave conversation in
                your account.
              </Text>
            </div>
            <Fieldset>
              <Controller<FormType, 'instructions'>
                name="instructions"
                render={({ field: { name, value, onChange, onBlur } }) => (
                  <Field name={name}>
                    <Field.Label>Instructions for Dave</Field.Label>
                    <MarkdownTextarea
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      aria-label="Instructions for Dave"
                      placeholder="e.g. We are a heat pump installer exclusively. Don't create new tags without asking first."
                    />
                    <div className="text-right">
                      <Text
                        as="span"
                        level="supporting"
                        colour={
                          value.length > CHAT_AGENT_INSTRUCTIONS_MAX_LENGTH
                            ? 'negative'
                            : 'primary'
                        }
                      >
                        {value.length}/{CHAT_AGENT_INSTRUCTIONS_MAX_LENGTH}
                      </Text>
                    </div>
                  </Field>
                )}
              />
            </Fieldset>
            <Button
              type="submit"
              isProcessing={isPending}
              disabled={!isDirty}
              className="self-end"
            >
              Save
            </Button>
          </Card.Body>
        </Card>
      </CompanySettingsTabBase>
    </Form>
  );
};

export default CompanySettingsChatAgent;
